// Original code geometry, inspected against the manufacturer's product photo.
// Presentation proportions / rib counts / mounting are not measured product CAD.
// The photo shows an opaque dark cup, not a transparent view of the hidden sieve.
export const mrsPrefilterEvidence = Object.freeze({
  productCode: "MRS-00416",
  productUrl: "https://www.mrs-shop.com/vorfilter-fuer-beregnungsanlagen-150my-siebfilter",
  photoUrl: "https://www.mrs-shop.com/media/image/a6/fe/24/Vorfilter-f-r-Beregnungsanlagen-150my-Siebfilter_600x600.png",
  sourceType: "manufacturer-product-page-and-photo",
  sieveMicrometres: 150,
  maximumPressureBar: 12,
  publishedSizeMm: Object.freeze([70, 60]),
  offeredTubeDiametersMm: Object.freeze([8, 12]),
  installedTubeDiameterMm: null,
  geometry: "photo-proportions-not-measured-cad",
  installation: "before-pump-on-suction-side-not-high-pressure-side",
  flowDirection: "scene-routing-not-product-arrow-verified",
  hiddenSieve: "washable-stainless-steel-on-PE-support-not-exposed-in-photo"
});

export function createMrsPrefilterModel(THREE, { mesh, material }) {
  const root = new THREE.Group();
  root.name = "mrs-00416-photo-prefilter";
  root.userData.evidence = mrsPrefilterEvidence;
  const dark = material(0x273039, { roughness: .68, metalness: .015 });
  const collar = material(0x19232b, { roughness: .58, metalness: .015 });
  const edge = material(0x35404a, { roughness: .67, metalness: .015 });
  const recess = material(0x070c10, { roughness: 1, metalness: 0, side: THREE.DoubleSide });
  const named = (name, geometry, surface, parent = root, at = [0, 0, 0]) => {
    // All resources use the scene owner's registration and disposal hooks.
    const object = mesh(geometry, surface, parent, at);
    object.name = name;
    return object;
  };
  const cylinder = (name, radius, height, surface, at, parent = root, open = false) =>
    named(name, new THREE.CylinderGeometry(radius, radius, height, 32, 1, open), surface, parent, at);
  const lathe = (name, points, surface) => named(name,
    new THREE.LatheGeometry(points.map(([r, y]) => new THREE.Vector2(r, y)), 36), surface);

  const body = cylinder("prefilter-opaque-upper-body", .148, .17, dark, [0, .095, 0]);
  const shoulder = lathe("prefilter-rounded-upper-shoulder", [
    [.148, .173], [.147, .184], [.139, .199], [.119, .216], [.084, .232], [.040, .242], [0, .244]
  ], dark);
  const cup = lathe("prefilter-opaque-ribbed-cup", [
    [0, -.25], [.080, -.25], [.119, -.244], [.132, -.23], [.132, -.075], [.135, -.062]
  ], dark);
  const unionRing = cylinder("prefilter-wide-removable-union-ring", .164, .090, collar, [0, -.031, 0]);
  const upperSeam = cylinder("prefilter-upper-union-seam", .166, .014, edge, [0, .012, 0]);
  const lowerSeam = cylinder("prefilter-lower-union-seam", .164, .012, dark, [0, -.077, 0]);

  // The visible longitudinal ribbing is approximated, not a claimed rib count.
  const ribs = [];
  const addRibs = (prefix, count, radius, y, height, width, depth, surface) => {
    for (let index = 0; index < count; index += 1) {
      const angle = index * Math.PI * 2 / count;
      const rib = named(`${prefix}-${index + 1}`, new THREE.BoxGeometry(width, height, depth), surface,
        root, [Math.sin(angle) * radius, y, Math.cos(angle) * radius]);
      rib.rotation.y = angle;
      ribs.push(rib);
    }
  };
  addRibs("prefilter-cup-rib", 18, .132, -.158, .156, .014, .009, collar);
  addRibs("prefilter-union-grip", 24, .163, -.032, .073, .012, .008, dark);

  // The upper elbow and lower side socket are both visible in the source photo.
  // Mirroring towards tank/pump is a drawing choice, not a verified flow arrow.
  const elbowCurve = new THREE.CubicBezierCurve3(
    new THREE.Vector3(.035, .215, 0), new THREE.Vector3(.035, .29, 0),
    new THREE.Vector3(.095, .29, 0), new THREE.Vector3(.18, .29, 0)
  );
  const elbow = named("prefilter-upper-right-angle-connection", new THREE.TubeGeometry(elbowCurve, 16, .046, 12, false), dark);
  const sideNeck = cylinder("prefilter-side-connection-neck", .048, .075, dark, [-.1775, .11, 0]);
  sideNeck.rotation.z = Math.PI / 2;

  const fittings = [];
  const makeFitting = (name, sign, y) => {
    const group = new THREE.Group(); group.name = `prefilter-${name}-push-fit`;
    root.add(group);
    const pieces = [
      { suffix: "socket-body", center: .231, length: .102, radius: .063, surface: dark },
      { suffix: "grip-band", center: .266, length: .029, radius: .070, surface: collar },
      { suffix: "release-collar", center: .300, length: .040, radius: .065, surface: edge }
    ].map(({ suffix, center, length, radius, surface }) => {
      const piece = cylinder(`prefilter-${name}-${suffix}`, radius, length, surface, [sign * center, y, 0], group, true);
      piece.rotation.z = Math.PI / 2;
      return piece;
    });
    const rim = named(`prefilter-${name}-open-mouth`, new THREE.RingGeometry(.040, .065, 32), collar,
      group, [sign * .32, y, 0]);
    rim.rotation.y = sign * Math.PI / 2;
    const inner = cylinder(`prefilter-${name}-recessed-bore`, .040, .041, recess, [sign * .2995, y, 0], group, true);
    inner.rotation.z = Math.PI / 2;
    const anchor = new THREE.Object3D(); anchor.name = `prefilter-${name}-hose-anchor`;
    anchor.position.set(sign * .32, y, 0); group.add(anchor);
    anchor.userData = { placement: "drawing-coordinate-not-connector-size", tubeVariant: "8-or-12-mm-not-confirmed" };
    const fitting = { group, pieces, rim, inner, anchor, outward: new THREE.Vector3(sign, 0, 0) };
    fittings.push(fitting);
    return fitting;
  };
  const inlet = makeFitting("inlet", 1, .29);
  const outlet = makeFitting("outlet", -1, .11);

  return {
    root, body, shoulder, cup, unionRing, upperSeam, lowerSeam, ribs, elbow, elbowCurve, sideNeck, fittings,
    inlet: inlet.anchor, outlet: outlet.anchor,
    ports: { inlet: inlet.anchor, outlet: outlet.anchor },
    inletDirection: inlet.outward, outletDirection: outlet.outward
  };
}
